import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { DollarSign, Loader2, Save } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";

export function PlanPriceEditor({ tiers, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/billing/prices").then((r) => setForm(r.data.prices)).catch(() => {}); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/billing/prices", { prices: form });
      setForm(r.data.prices);
      toast.success("Prices saved. New subscriptions use the new prices.");
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save prices"); } finally { setSaving(false); }
  };

  return (
    <motion.div data-testid="plan-price-editor" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="mb-8 bg-card border border-amber-500/30 rounded-2xl p-6">
      <h2 className="font-display text-base font-semibold flex items-center gap-2 mb-1"><DollarSign className="h-4 w-4 text-amber-400" /> Monthly subscription prices</h2>
      <p className="text-xs text-gray-500 mb-4">Saved to Stripe automatically. Changes apply to new subscriptions; current subscribers keep their price.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        {tiers.map((t) => (
          <div key={t.slug} className="space-y-1.5">
            <Label className="text-gray-300 text-sm">{t.name}</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
              <Input data-testid={`price-input-${t.slug}`} type="number" min="1" step="0.01" value={form[t.slug] ?? ""} onChange={(e) => setForm({ ...form, [t.slug]: e.target.value })} className="bg-gray-950 border-gray-800 pl-7 h-10" />
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-end mt-4">
        <button data-testid="save-plan-prices" onClick={save} disabled={saving} className="gold-gradient text-black font-semibold px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save prices
        </button>
      </div>
    </motion.div>
  );
}
